import { Component, HostListener, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { IAppState } from 'app/store/app/app.state';
import {
  SetActiveNavMenuIds,
  SetSelectedPrimaryNavConfig,
} from 'app/store/sidebar/sidebar.actions';
import { selectSelectedPrimaryNavConfig } from 'app/store/sidebar/sidebar.selectors';
import { HasSubscriptions } from 'shared/components/higher-order/has-subscriptions';
import { LABELS } from 'shared/consts/labels.const';
import {
  applicationMenuConfig,
  InfoPostNavConfig,
  TSPEnum,
  lowerInfoPostNavMenuConfigs,
  FeatureFlag,
} from './nav-menus.constants';
import { Observable } from 'rxjs';
import { IInfoPostState } from '../../../../store/infopost/infopost.state';
import { getFeatureFlageInfo, getSelectedInfoPostTSP } from '../../../../store/infopost/infopost.selectors';
import { ServiceProvider } from '@gms/tsp-api';
import { filter, map, take } from 'rxjs/operators';
import orderBy from 'lodash/orderBy';
import { InformationalWebLabels } from 'app/modules/infopost/informational-posting-web.utils';
import { NavConfig } from '../../sidebar-nav/nav-menus.constants';
import { buildAclList } from '../../sidebar-nav/menus.utils';
import { RolesService } from 'shared/services/roles.service';

@Component({
  selector: 'app-infopost-sidebar-nav',
  templateUrl: './sidebar-nav.component.html',
  styleUrls: ['./sidebar-nav.component.scss'],
})
export class InfoPostSidebarNavComponent extends HasSubscriptions implements OnInit {
  CONST_LABELS = LABELS;

  @Input() menuItemType: string = this.CONST_LABELS.MENU_TYPE_ADMIN;

  @Input() isAccessPage: boolean = true;

  upperNavMenuConfigs: InfoPostNavConfig[];

  lowerNavMenuConfigs: InfoPostNavConfig[];

  upperNavMenuConfigsFilterList: InfoPostNavConfig[];
  menuGuards: { [name: string]: Observable<boolean> } = {};

  selectedTsp$: Observable<ServiceProvider> = this._infoStore.pipe(
    select(getSelectedInfoPostTSP),
    map((data) => {
      return data;
    })
  );

  featureFlagInfo$: Observable<Array<FeatureFlag>> = this._store.pipe(
    select(getFeatureFlageInfo),
    map((el) => el.filter(data => data.name === InformationalWebLabels.CBHFEATUREFLAG))
  );

  selectedTspInfo: ServiceProvider;
  secondaryImage: string;
  redirectionLink: string;

  private customerActivities: InfoPostNavConfig =   {
    id: 'customer-activities-main1',
    label: 'Customer Activities',
    route: '/home',
    icon: 'icon-customer-activities',
    refID: 'BISA',
    displayOrder: 101,
    tspApplicable: [TSPEnum.OTHER],
    isapplicableDownload: false,
    skipAclCheck: true
  }

  constructor(
    private _store: Store<IAppState>,
    private _infoStore: Store<IInfoPostState>,
    private _router: Router,
    private rolesService: RolesService,
  ) {
    super();

    this.addSubscriptions([
      this.selectedTsp$.subscribe((resp) => {
        this.selectedTspInfo = resp;
        const { secondaryImage, redirectionLink } = this.updateLogoSelectedTsp(this.selectedTspInfo?.providerId);
        this.secondaryImage = secondaryImage;
        this.redirectionLink = redirectionLink;
        this.getUpperNavMenuConfig();
      }),
    ]);
  }

  getUpperNavMenuConfig() {
    let upperNavMenuConfigsMain = applicationMenuConfig
      .filter((x) => !x.subMenuItems || x.subMenuItems.length === 0)
      .filter(
        (x) =>
          x.refID === this.menuItemType &&
          ((this.menuItemType === this.CONST_LABELS.MENU_TYPE_MAIN) ||
            (x.tspApplicable?.includes(this.selectedTspInfo?.providerId)) || (x.tspApplicable.includes(-1)))
      );

    let upperNavMenuConfigsSub = applicationMenuConfig
      .filter((x) => x.subMenuItems && x.subMenuItems.length > 0 && x.refID === this.menuItemType)
      .map((data) => ({
        ...data,
        subMenuItems: data.subMenuItems.filter((sub) =>
          sub.tspApplicable?.includes(this.selectedTspInfo?.providerId) || (sub.tspApplicable.includes(-1))
        ),
      }));

    let upperNavMenuConfigsTerriterySub = applicationMenuConfig
      .filter((x) => x.subMenuItems && x.subMenuItems.length > 0 && x.refID === this.menuItemType)
      .map((data) => ({
        ...data,
        subMenuItems: data.subMenuItems
          .filter((sub) => sub.subMenuItems)
          .some((x) => x.tspApplicable?.includes(this.selectedTspInfo?.providerId) || (x.tspApplicable.includes(-1))),
      }));
    const defaultItems =
      [...applicationMenuConfig, ...lowerInfoPostNavMenuConfigs].filter(
        (x) =>
          x.refID === this.menuItemType &&
          (this.menuItemType === this.CONST_LABELS.MENU_TYPE_MAIN ||
            x.tspApplicable?.includes(TSPEnum.LOUISIANA))
      );
    const idsToRemove = ['rates', 'NAESB-code-values'];
    let upperNavMenuConfigs = [...upperNavMenuConfigsMain, ...upperNavMenuConfigsSub].filter(
      (item) => !idsToRemove.includes(item.id),
      ...upperNavMenuConfigsTerriterySub
    );

    const filteredLOUISIANATSPItems = upperNavMenuConfigs.filter(
      (item) => item.tspApplicable && item.tspApplicable.includes(this.selectedTspInfo?.providerId))

    this.upperNavMenuConfigs = this.getMenuItems(defaultItems, filteredLOUISIANATSPItems, upperNavMenuConfigs)

    this.getLowerNavMenuConfig();
  }

  getMenuItems(defaultItems, filteredLOUISIANATSPItems, upperNavMenuConfigs) {

    let data: InfoPostNavConfig[];

    if (Object.keys(this.selectedTspInfo ?? {}).length === 0 && (defaultItems && defaultItems.length > 0)) {
      data = orderBy(defaultItems, [InformationalWebLabels.DISPLAYORDER], [InformationalWebLabels.SORTASCENDING]);
    }
    else if (this.selectedTspInfo?.providerId === TSPEnum.LOUISIANA) {
      data = orderBy(filteredLOUISIANATSPItems, [InformationalWebLabels.DISPLAYORDER], [InformationalWebLabels.SORTASCENDING]);
    }
    else {
      data = orderBy(upperNavMenuConfigs, [InformationalWebLabels.DISPLAYORDER], [InformationalWebLabels.SORTASCENDING]);
    }

    return data;
  }

  getLowerNavMenuConfig() {
    let lowerNavMenuConfigsMain = lowerInfoPostNavMenuConfigs
      .filter((x) => !x.subMenuItems || x.subMenuItems.length === 0)
      .filter(
        (x) =>
          x.refID === this.menuItemType && (x.tspApplicable?.includes(this.selectedTspInfo?.providerId) || x.tspApplicable.includes(-1))
      );

    let lowerNavMenuConfigsSub = lowerInfoPostNavMenuConfigs
      .filter((x) => x.subMenuItems && x.subMenuItems.length > 0 && x.refID === this.menuItemType)
      .map((data) => ({
        ...data,
        subMenuItems: data.subMenuItems.filter((sub) =>
          sub.tspApplicable?.includes(this.selectedTspInfo?.providerId)
        ),
      }));

    this.lowerNavMenuConfigs = [...lowerNavMenuConfigsMain, ...lowerNavMenuConfigsSub];

    this.updateMenuItemConfig();
  }

  ngOnInit() {
    this.getUpperNavMenuConfig();

    // Get currently active route
    this.addSubscriptions([
      this._router.events.subscribe((event: NavigationEnd): void => {
        let url: string = '';
        if ((event as any)?.routerEvent) {
          url = (event as any)?.routerEvent.url;
        } else if (event.urlAfterRedirects) {
          url = event.urlAfterRedirects;
        }

        if (url === '/') {
          this._router.navigate(['/main']);
          url = '';
        }

        if (url) {
          if (url.lastIndexOf('/new') > -1) {
            let idx = url.lastIndexOf('/');
            if (idx > -1) {
              url = url.substring(0, idx);
            }
          }

          let regexWithID = /\/manual-posting\/(\w+)$/;
          let regexWithEdit = /\/edit$/;

          // Check if the URL contains a dynamic ID but does not end with "/edit"
          if (regexWithID.test(url) && !regexWithEdit.test(url)) {
            if (regexWithID.test(url)) {
              if (url.lastIndexOf('/manual-posting/') > -1) {
                let idx = url.lastIndexOf('/');
                if (idx > -1) {
                  url = url.substring(0, idx);
                }
              }
            }
          }

          // Regular expression to match the pattern "/manual-posting/{id}/edit"
          let regexForEdit = /\/manual-posting\/(\w+)\/edit/;
          if (regexForEdit.test(url)) {
            if (url.lastIndexOf('/edit') > -1) {
              let editIdx = url.lastIndexOf('/edit');
              let idIdx = url.lastIndexOf('/', editIdx - 1);
              if (idIdx > -1) {
                url = url.substring(0, idIdx);
              }
            }
          }

          let item = applicationMenuConfig
            .filter((x) => x.route === url)
            .filter((x) => x.nextRefID);
          if (item && item.length > 0) this.menuItemType = item[0].nextRefID;
          else {
            let item = applicationMenuConfig.filter((x) => x.route === url);
            if (item && item.length > 0) this.menuItemType = item[0]?.refID;
            else {
              let secondUrl = applicationMenuConfig.filter((x) =>
                x.subMenuItems?.some((y) => y.route === url)
              );
              if (secondUrl && secondUrl.length > 0) this.menuItemType = secondUrl[0]?.refID;
              else {
                let parentMenuConfig = applicationMenuConfig.filter(
                  (parent) => parent.subMenuItems && parent.subMenuItems.length > 0,
                );

                // Filter to keep only those items with non-empty subMenuItems
                let childMenuConfig = parentMenuConfig
                  .map((parent) => ({
                    ...parent,
                    subMenuItems: parent.subMenuItems.filter(
                      (child) => child.subMenuItems && child.subMenuItems.length > 0
                    ),
                  }))
                  .filter((parent) => parent.subMenuItems.length > 0);

                let child = childMenuConfig.filter((x) =>
                  x.subMenuItems.filter((x) => x.route === url)
                );

                if (child && child.length > 0) {
                  let tertiaryMenuConfig = child
                    .filter((x) => x.subMenuItems)
                    .filter((x) =>
                      x?.subMenuItems?.filter((y) => y.subMenuItems.some((y) => y.route === url))
                    );
                  if (tertiaryMenuConfig && tertiaryMenuConfig.length > 0)
                    this.menuItemType = tertiaryMenuConfig[0]?.refID;
                } else {
                  let lowerUrl = lowerInfoPostNavMenuConfigs.filter((x) => x.route === url);
                  if (lowerUrl && lowerUrl.length > 0) this.menuItemType = lowerUrl[0]?.refID;
                  else {
                    let lowerUrl = lowerInfoPostNavMenuConfigs.filter((x) =>
                      x.subMenuItems?.some((y) => y.route === url)
                    );
                    if (lowerUrl && lowerUrl.length > 0) this.menuItemType = lowerUrl[0]?.refID;
                  }
                }
              }
            }
          }

          this.getUpperNavMenuConfig();
        }

        if (event.urlAfterRedirects) {
          const activeRoute = event.urlAfterRedirects;
          let activePrimaryRoute = null;
          let activeSecondaryRoute = null;
          let activeTertiaryRoute = null;

          if (!this.upperNavMenuConfigs) {
            this.getUpperNavMenuConfig();
          }
          // Figure out active route hierarchy
          this.upperNavMenuConfigs.forEach((primaryMenuConfig) => {
            // Check primary route
            if (
              primaryMenuConfig.route &&
              this.routeIsActive(primaryMenuConfig.route, activeRoute)
            ) {
              activePrimaryRoute = primaryMenuConfig.id;
              activeSecondaryRoute = null;
              activeTertiaryRoute = null;
            }

            // Check secondary routes
            if (Array.isArray(primaryMenuConfig.subMenuItems)) {
              primaryMenuConfig.subMenuItems.forEach((secondaryMenuConfig) => {
                if (
                  secondaryMenuConfig.route &&
                  this.routeIsActive(secondaryMenuConfig.route, activeRoute)
                ) {
                  activePrimaryRoute = primaryMenuConfig.id;
                  activeSecondaryRoute = secondaryMenuConfig.id;
                  activeTertiaryRoute = null;
                }

                // Check tertiary routes
                if (Array.isArray(secondaryMenuConfig.subMenuItems)) {
                  secondaryMenuConfig.subMenuItems.forEach((tertiaryMenuConfig) => {
                    if (
                      tertiaryMenuConfig.route &&
                      this.routeIsActive(tertiaryMenuConfig.route, activeRoute)
                    ) {
                      activePrimaryRoute = primaryMenuConfig.id;
                      activeSecondaryRoute = secondaryMenuConfig.id;
                      activeTertiaryRoute = tertiaryMenuConfig.id;
                    }
                  });
                }
              });
            }
          });

          this._store.dispatch(
            new SetActiveNavMenuIds({
              primary: activePrimaryRoute,
              secondary: activeSecondaryRoute,
              tertiary: activeTertiaryRoute,
            })
          );
        }
      }),
    ]);

    // Get Selected Menu NavConfig
    this.addSubscription(
      this._store.pipe(select(selectSelectedPrimaryNavConfig)).subscribe((config) => {
        this.selectedPrimaryNavConfig = config;
        this.selectedPrimaryNavConfigHasSecondary =
          this.selectedPrimaryNavConfig &&
          Array.isArray(this.selectedPrimaryNavConfig.subMenuItems);

        if (this.selectedPrimaryNavConfig?.nextRefID) {
          this.menuItemType = this.selectedPrimaryNavConfig.nextRefID;
          this.getUpperNavMenuConfig();
        }
      })
    );

    this.handleFeatureFlag();
  }

  private handleFeatureFlag() {
    this.upperNavMenuConfigsFilterList = applicationMenuConfig.filter(x => x.id === LABELS.MENU_TYPE_GAS_QUALITY);

    this.addSubscription(
      this.featureFlagInfo$.subscribe(featureFlags => {
        if (!featureFlags || featureFlags.length === 0) {
          return;
        }
        this.upperNavMenuConfigsFilterList.forEach((item: InfoPostNavConfig) => {
          if (item.id === LABELS.MENU_TYPE_GAS_QUALITY && item.subMenuItems.length > 0) {
            item.subMenuItems = item.subMenuItems.filter(subItem => {
              if (subItem.id === LABELS.MENU_TYPE_CBH && subItem.subMenuItems?.length > 0) {
                return this.isFeatureEnabledForSubItem(subItem.featureName, featureFlags);
              }
              return true;
            });
          }
        });
      })
    )
  }
  private isFeatureEnabledForSubItem(subMenuItemName: string, featureFlags: FeatureFlag[]): boolean {
    const featureFlag = featureFlags.find(flag => flag.name === subMenuItemName);
    return featureFlag ? featureFlag.isEnabled : false;
  }

  selectedPrimaryNavConfig: InfoPostNavConfig = null;
  selectedPrimaryNavConfigHasSecondary = false;

  @HostListener('document:mouseleave')
  onMouseLeave() {
    if (this.selectedPrimaryNavConfig !== null) {
      this._store.dispatch(new SetSelectedPrimaryNavConfig(null));
    }
  }

  clearSelectedPrimaryNavConfig() {
    this._store.dispatch(new SetSelectedPrimaryNavConfig(null));
  }

  private routeIsActive(expectedRoute: string, actualRoute: string): boolean {
    return (
      expectedRoute &&
      actualRoute &&
      (actualRoute.startsWith(expectedRoute) ||
        (expectedRoute.endsWith('/') && actualRoute.startsWith(expectedRoute.slice(0, -1))))
    );
  }

  private buildMenuGuard(menuConfig: NavConfig) {
    if (!menuConfig.subMenuItems) return;
    menuConfig.subMenuItems.forEach(subMenuConfig => {
      this.menuGuards[subMenuConfig.id] = this.rolesService
        .hasResourceAccess(buildAclList(subMenuConfig))
        .pipe(take(1));
      this.buildMenuGuard(subMenuConfig);
    });
  }

  private updateMenuItemConfig() {
    Object.values(this.upperNavMenuConfigs).forEach(menuConfig => {
      this.menuGuards[menuConfig.id] = this.rolesService
        .hasResourceAccess(buildAclList(menuConfig))
        .pipe(take(1));
      this.buildMenuGuard(menuConfig);
    });

    if(this._router.url.includes(LABELS.BISA_ROUTE_URL)){
      this.addSubscription(
        this.rolesService.roles().pipe(
          filter(roles => roles.filter(x => !x.includes(LABELS.BISA_ROLE_NAME)).length > 0),
        ).subscribe(
          () => this.lowerNavMenuConfigs.unshift(this.customerActivities),
        ),
      );
    }

    Object.values(this.lowerNavMenuConfigs).forEach(menuConfig => {
      this.menuGuards[menuConfig.id] = this.rolesService
        .hasResourceAccess(buildAclList(menuConfig))
        .pipe(take(1));
      if (menuConfig.subMenuItems) {
        menuConfig.subMenuItems.forEach(subMenuConfig => {
          this.menuGuards[subMenuConfig.id] = this.rolesService
            .hasResourceAccess(buildAclList(subMenuConfig))
            .pipe(take(1));
        });
      }
    });
  }

  updateLogoSelectedTsp(providerId: number) {
    let secondaryImage: string;
    let redirectionLink: string;
    if (!this._router.url.includes(LABELS.BISA_ROUTE_URL)) {
      if (this._router.url.includes(InformationalWebLabels.MAINPAGEROUTE)) {
        secondaryImage = '/assets/svg/Boardwalk-Pipelines-Logo.svg'
        redirectionLink = this.CONST_LABELS.BOARD_WALK_PIPELINE_URL;
      } else {
        switch (providerId) {
          case TSPEnum.GULFSOUTH:
            secondaryImage = '/assets/svg/Gulf-South-Pipeline-Logo.svg';
            redirectionLink = this.CONST_LABELS.GULF_SOUTH_URL;
            break;
          case TSPEnum.BOARDWALK:
            secondaryImage = '/assets/svg/Boardwalk-Storage-Logo.svg';
            redirectionLink = this.CONST_LABELS.BOARD_WALK_STORAGE_URL;
            break;
          case TSPEnum.TEXAS:
            secondaryImage = '/assets/svg/Texas-Gas-Transmission-Logo.svg';
            redirectionLink = this.CONST_LABELS.TEXAS_GAS_URL;
            break;
        }
      }
    }

    return { secondaryImage, redirectionLink };
  }

}
